import React from 'react';
import { BRAND } from '../lib/brand';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack
} from '@mui/material';
import {
  AutoAwesome,
  Assessment,
  VerifiedUser,
  Warning,
  Chat,
  Handyman,
  CheckCircle,
  ArrowForward,
  TrendingUp,
  Public,
  Shield
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

// Organization + SoftwareApplication schema.org structured data for the
// homepage - the entity Google's other signals (title, sitelinks, knowledge
// panel) get attached to. Only real, checkable claims: the Free plan really
// is $0, sameAs only lists profiles that actually exist. No aggregateRating/
// review markup - we don't have real review data, and fabricating one is
// exactly the kind of structured-data abuse Google's guidelines call out.
const HOMEPAGE_JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: BRAND.productName,
      url: BRAND.canonicalUrl,
      logo: `${BRAND.canonicalUrl}/logo-mark.svg`,
      description: "AI-powered cybersecurity policy and compliance platform for teams without a dedicated compliance department.",
      sameAs: [BRAND.social.twitter, BRAND.social.linkedin, BRAND.social.github],
    },
    {
      '@type': 'SoftwareApplication',
      name: BRAND.productName,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: "Create, customize, and manage your organization's cybersecurity policies with AI - mapped to NIST, SOC 2, ISO 27001, GDPR, and HIPAA.",
      url: BRAND.canonicalUrl,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Free plan - 3 AI generations/day, 1 seat, all free assessment tools',
      },
    },
  ],
};

const Landing = () => {
  useDocumentMeta({
    title: 'AI Security Policies & Compliance Software',
    description: "Create, customize, and manage your organization's cybersecurity policies with AI - mapped to NIST, SOC 2, ISO 27001, GDPR, and HIPAA.",
    path: '/',
    jsonLd: HOMEPAGE_JSON_LD,
  });

  // Footer links use /#features and /#pricing; React Router doesn't scroll
  // to hashes on its own. Only treat the hash as a scroll target when it
  // looks like a real anchor id - an OAuth redirect that lands here instead
  // of /oauth/callback leaves a token hash (#access_token=...) in the URL,
  // and passing that to querySelector throws (invalid selector), crashing
  // the whole page past the ErrorBoundary.
  React.useEffect(() => {
    if (/^#[a-zA-Z][\w-]*$/.test(window.location.hash)) {
      document.querySelector(window.location.hash)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const navigate = useNavigate();

  const handleGetStarted = async () => {
    // The Redux organization state is never populated on public routes -
    // the real signed-in check is the persisted Supabase session.
    const { data: { session } } = await supabase.auth.getSession();
    navigate(session ? '/dashboard' : '/signup');
  };

  const features = [
    {
      icon: <AutoAwesome />,
      title: 'Policy generation',
      description: 'NIST, SOC 2, ISO 27001 and more, written for you in minutes — then refined with an AI editor when you need to tweak them.'
    },
    {
      icon: <VerifiedUser />,
      title: 'Real control mapping',
      description: "See exactly what's covered — computed from your policies, not guessed."
    },
    {
      icon: <Assessment />,
      title: 'Gap analysis',
      description: 'Plain-language answers on where your policies fall short.'
    },
    {
      icon: <TrendingUp />,
      title: 'Risk register & board reports',
      description: 'Track risks with severity scoring, then turn them into a report your board can actually read.'
    },
    {
      icon: <Public />,
      title: 'Trust pages',
      description: 'A public, shareable page with your security posture and approved policies — set up in minutes.'
    },
    {
      icon: <Warning />,
      title: 'Incident & threat tracking',
      description: 'Every disclosed SEC cyber incident plus actively exploited CVEs, pulled daily.'
    },
    {
      icon: <Chat />,
      title: 'vCISO assistant',
      description: 'A security advisor on demand — frameworks, incidents, vendor questionnaires, your own policies.'
    },
    {
      icon: <Shield />,
      title: 'Insurance renewal readiness',
      description: 'Know what underwriters will ask for before they ask.'
    },
    {
      icon: <Handyman />,
      title: 'Free assessment tools',
      description: 'Attack surface, ransomware readiness, breach cost, and more — seven tools, no login required.'
    }
  ];

  const pricingTiers = [
    {
      name: 'Free',
      price: '$0',
      period: '',
      description: 'Kick the tires - no credit card',
      features: [
        '3 AI generations/day',
        '1 seat',
        'AI policy generator & vCISO assistant',
        'All 7 free assessment tools',
        'SEC incident & threat trackers'
      ],
      cta: 'Start free',
      popular: false
    },
    {
      name: 'Starter',
      price: '$99',
      period: '/month',
      description: 'For seed-stage teams getting their first policies in place',
      features: [
        '25 AI generations/day',
        '3 team seats',
        'All policy types & frameworks',
        'AI gap analysis & roadmaps',
        'Risk register',
        'Email support'
      ],
      cta: 'Get started',
      popular: false
    },
    {
      name: 'Growth',
      price: '$499',
      period: '/month',
      description: 'For mid-market teams facing SOC 2, ISO 27001, or HIPAA',
      features: [
        'Everything in Starter',
        '300 AI generations/day (fair use)',
        '25 team seats',
        'Compliance control mapping',
        'Trust pages & board reports',
        'Priority support'
      ],
      cta: 'Get started',
      popular: true
    },
    {
      name: 'Scale',
      price: '$1,500+',
      period: '/month',
      description: 'For Series B+ and enterprise programs',
      features: [
        'Everything in Growth',
        '1,000 AI generations/day',
        'Unlimited team seats',
        'Custom risk scoring & insurance renewal readiness',
        'Dedicated onboarding & security review support'
      ],
      cta: 'Contact us',
      mailto: `mailto:${BRAND.supportEmail}?subject=Scale%20plan`,
      popular: false
    }
  ];

  return (
    <Box>
      {/* Hero */}
      <Container maxWidth="md" sx={{ pt: { xs: 8, md: 12 }, pb: 6 }}>
        <Typography
          variant="overline"
          sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 2 }}
        >
          For teams who'd rather be building
        </Typography>
        <Typography
          variant="h1"
          component="h1"
          sx={{ fontSize: { xs: '2.25rem', md: '3.125rem' }, lineHeight: 1.2, mb: 3, maxWidth: 720 }}
        >
          You don't need a compliance department. You need this.
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mb: 4, maxWidth: 560, lineHeight: 1.65 }}>
          Answer a few questions, get real security policies in minutes. {BRAND.productName} explains
          every step in plain language — no compliance degree required.
        </Typography>
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            size="large"
            onClick={handleGetStarted}
            endIcon={<ArrowForward />}
            sx={{ py: 1.5, px: 3.5, fontSize: '1rem' }}
          >
            Start free — no credit card
          </Button>
          <Button
            component="a"
            href="/tools"
            variant="text"
            color="secondary"
            size="large"
            sx={{ fontSize: '1rem', borderBottom: '2px solid', borderRadius: 0, borderColor: 'secondary.main', pb: 0.25 }}
          >
            Browse the free tools
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 5 }}>
          7 free tools &nbsp;·&nbsp; 9 frameworks covered &nbsp;·&nbsp; updated with every SEC filing
        </Typography>
      </Container>

      {/* Features */}
      <Container maxWidth="md" sx={{ pt: 2, pb: 2 }}>
        <Typography id="features" variant="h4" component="h2" gutterBottom sx={{ scrollMarginTop: 96, mb: 1 }}>
          Everything you need, none of the jargon
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 5, maxWidth: 560 }}>
          One place to generate policies, track risk, report to your board, and keep an eye on
          what's happening outside your walls.
        </Typography>
      </Container>
      <Container maxWidth="md" sx={{ pb: 10 }}>
        <Grid container spacing={2.5}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2.75 }}>
                  <Box sx={{ color: 'secondary.main', mb: 1.5 }}>{feature.icon}</Box>
                  <Typography
                    variant="subtitle1"
                    component="h3"
                    sx={{ fontWeight: 600, mb: 1, display: 'inline-block', borderBottom: '2px solid', borderColor: 'divider', pb: 0.25 }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Pricing */}
      <Box sx={{ bgcolor: '#F4ECDA', py: 10 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography id="pricing" variant="h4" component="h2" gutterBottom sx={{ scrollMarginTop: 96 }}>
              Simple, transparent pricing
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Start free — no credit card required. Upgrade when you need more volume and seats.
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            {pricingTiers.map((tier, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    bgcolor: 'background.paper',
                    border: tier.popular ? '2px solid' : '1px solid',
                    borderColor: tier.popular ? 'primary.main' : 'divider'
                  }}
                >
                  {tier.popular && (
                    <Chip
                      label="Most Popular"
                      color="primary"
                      size="small"
                      sx={{ position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)' }}
                    />
                  )}
                  <CardContent sx={{ p: 3.5, pt: tier.popular ? 5 : 3.5 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 0.5 }}>{tier.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, minHeight: 40 }}>
                      {tier.description}
                    </Typography>
                    <Box sx={{ mb: 2.5 }}>
                      <Typography component="span" sx={{ fontFamily: '"Lora", serif', fontWeight: 700, fontSize: '1.875rem' }}>
                        {tier.price}
                      </Typography>
                      <Typography component="span" variant="body2" color="text.secondary">
                        {tier.period}
                      </Typography>
                    </Box>
                    <Button
                      variant={tier.popular ? 'contained' : 'outlined'}
                      color={tier.popular ? 'primary' : 'secondary'}
                      fullWidth
                      {...(tier.mailto
                        ? { component: 'a' as const, href: tier.mailto }
                        : { onClick: handleGetStarted })}
                      sx={{ mb: 2.5 }}
                    >
                      {tier.cta}
                    </Button>
                    <Stack spacing={1.25}>
                      {tier.features.map((feature, featureIndex) => (
                        <Box key={featureIndex} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <CheckCircle fontSize="small" color="secondary" sx={{ mt: 0.25 }} />
                          <Typography variant="body2">{feature}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Final CTA */}
      <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h4" component="h2" gutterBottom>
          Ready to get your policies in order?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Start free, no credit card required. Upgrade only when you need more volume or seats.
        </Typography>
        <Button variant="contained" size="large" onClick={handleGetStarted} sx={{ py: 1.5, px: 4, fontSize: '1rem' }}>
          Start free
        </Button>
      </Container>
    </Box>
  );
};

export default Landing;
